import { useMemo } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Icon, IconButton } from '@atom/mui';
import { DeleteIcon, EditIcon } from '@atom/mui/icons';
import { MaterialEntry } from '@atom/types/materialEntry';
import { TaskMaterialItem, TaskUser } from '@atom/types/task';
import { WorkOrderDetailType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import classes from './materialModal.module.css';

export interface Props {
  materialEntry: MaterialEntry;
  material: TaskMaterialItem;
  workOrderDetail: WorkOrderDetailType;
  availableUsers: TaskUser[];
  editMaterialEntryId: string;
  createMaterialEntry: boolean;
  setEditMaterialEntryId: (id: string) => void;
  handleMaterialEntryDelete: (id: string) => void;
}

const MaterialEntryDisplayRow = ({
  materialEntry,
  availableUsers,
  material,
  workOrderDetail,
  editMaterialEntryId,
  createMaterialEntry,
  setEditMaterialEntryId,
  handleMaterialEntryDelete,
}: Props) => {
  const userProfile = useUserProfile();

  const convertNumberToLocaleString = (input: string | number): string => {
    return Number(input).toLocaleString('en-US');
  };

  const user = availableUsers.find(
    availableUser => availableUser.id === materialEntry.userId,
  );

  const cost = useMemo(() => {
    const rawCost =
      R.pathOr(0, ['rate'], material) * R.pathOr(0, ['amount'], materialEntry);

    return numberToLocaleString(rawCost.toFixed(2));
  }, [material, materialEntry]);

  const canDeleteOrEdit =
    userProfile.userId === materialEntry.userId
      ? hasRolePermissions(ROLE_SETS.INSPECTOR)
      : hasRolePermissions(ROLE_SETS.MANAGER);

  const isEditDisabled =
    !!editMaterialEntryId ||
    createMaterialEntry ||
    workOrderDetail.isClosed ||
    !canDeleteOrEdit;
  const isDeleteDisabled =
    !!editMaterialEntryId ||
    createMaterialEntry ||
    workOrderDetail.isClosed ||
    !canDeleteOrEdit;

  return (
    <div className={classes.entryRow}>
      <div className={classes.entryInfoSection}>
        <div className={clsx(classes.displayContainer, classes.name)}>
          <div className={classes.label}>Team Member</div>
          <div> {`${user?.firstName} ${user?.lastName}`}</div>
        </div>
        <div className={classes.displayContainer}>
          <div className={classes.label}>Date</div>
          <div>{setDisplayDate(materialEntry?.date)}</div>
        </div>
        {material?.isStartEndReading && (
          <>
            <div className={classes.displayContainer}>
              <div className={classes.label}>Start Reading</div>
              <div>{materialEntry?.startReading ?? <span>&ndash;</span>}</div>
            </div>
            <div className={classes.displayContainer}>
              <div className={classes.label}>End Reading</div>
              <div>{materialEntry?.endReading ?? <span>&ndash;</span>}</div>
            </div>
          </>
        )}
        <div className={classes.displayContainer}>
          <div className={classes.label}>
            <span>Used (</span>
            <span className={classes.materialUnit}>{material?.unit}</span>
            <span>)</span>
          </div>
          <div>
            {convertNumberToLocaleString(materialEntry?.amount.toFixed(2))}
          </div>
        </div>
        <div className={clsx(classes.displayContainer, classes.cost)}>
          <div className={classes.label}>Cost</div>
          <div>{cost}</div>
        </div>
      </div>
      <div className={classes.entryButtonSection}>
        <IconButton
          tooltip="Edit"
          onClick={() => setEditMaterialEntryId(materialEntry.id)}
          disabled={isEditDisabled}
          data-cy={`materialEntryEditButton-${materialEntry.id}`}
        >
          <Icon icon={EditIcon} />
        </IconButton>
        <IconButton
          tooltip="Delete"
          onClick={() => handleMaterialEntryDelete(materialEntry.id)}
          disabled={isDeleteDisabled}
          data-cy={`materialEntryDeleteButton-${materialEntry.id}`}
        >
          <Icon icon={DeleteIcon} />
        </IconButton>
      </div>
    </div>
  );
};

export default MaterialEntryDisplayRow;
